import { useState } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import { csrfToken } from "@/lib/fetchSideEffect";
import { DEFAULT_AI_CHAT_URL } from "@/lib/shareTask";
import { DEFAULT_DISCUSS_PROMPT } from "@/lib/discussPrompt";

interface AiAction {
  id: string;
  name: string;
  prompt_template: string;
  enabled: boolean;
  is_seeded: boolean;
  scope: "task" | "collection";
  confirm: boolean;
  can_grok: boolean;
  can_web: boolean;
  can_copy: boolean;
}

const inputClass =
  "w-full text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border border-gray-200 dark:border-gray-700 rounded-md px-2.5 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20";

const rowHeaderClass =
  "w-full flex items-center justify-between gap-3 p-4 text-left cursor-pointer group";

const editHintClass =
  "text-sm text-gray-500 group-hover:text-gray-800 dark:text-gray-400 dark:group-hover:text-gray-200 shrink-0";

const chatPresets: { label: string; url: string }[] = [
  { label: "Claude", url: "https://claude.ai/new?q={prompt}" },
  { label: "ChatGPT", url: "https://chatgpt.com/?q={prompt}" },
  { label: "Perplexity", url: "https://www.perplexity.ai/search?q={prompt}" },
];

function ScopeField({ defaultValue = "task" }: { defaultValue?: "task" | "collection" }) {
  return (
    <label className="block text-xs text-gray-500 dark:text-gray-400">
      Область применения
      <select name="ai_action[scope]" defaultValue={defaultValue} aria-label="Область применения" className={`mt-1 ${inputClass}`}>
        <option value="task">Задача — применяется к одной задаче и её подзадачам (например, разбить на шаги)</option>
        <option value="collection">Коллекция — применяется сразу ко всему набору: списку или подзадачам одной задачи (например, убрать дубли)</option>
      </select>
    </label>
  );
}

function ConfirmField({ defaultChecked = false }: { defaultChecked?: boolean }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
      <input type="hidden" name="ai_action[confirm]" value="0" />
      <input type="checkbox" name="ai_action[confirm]" value="1" defaultChecked={defaultChecked} />
      Показывать превью перед применением
    </label>
  );
}

function ChannelsField({ action }: { action?: Pick<AiAction, "can_grok" | "can_web" | "can_copy"> }) {
  const rows = [
    { name: "can_grok", label: "В приложении (Grok)", def: action?.can_grok ?? true },
    { name: "can_web", label: "Открыть в вебе", def: action?.can_web ?? true },
    { name: "can_copy", label: "Скопировать промпт", def: action?.can_copy ?? true },
  ];
  return (
    <fieldset className="space-y-1">
      <legend className="text-xs text-gray-500 dark:text-gray-400">Каналы в меню</legend>
      {rows.map((r) => (
        <label key={r.name} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <input type="hidden" name={`ai_action[${r.name}]`} value="0" />
          <input type="checkbox" name={`ai_action[${r.name}]`} value="1" defaultChecked={r.def} />
          {r.label}
        </label>
      ))}
    </fieldset>
  );
}

function ActionRow({ action }: { action: AiAction }) {
  const [open, setOpen] = useState(false);

  return (
    <li className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={rowHeaderClass}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-white break-words">
            {action.name}
          </span>
          {!action.enabled && (
            <span className="text-xs text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 shrink-0">
              Выкл
            </span>
          )}
        </span>
        <span className={editHintClass}>{open ? "Закрыть" : "Изменить"}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          <form method="POST" action={`/settings/ai_actions/${action.id}`} className="space-y-2">
            <input type="hidden" name="authenticity_token" value={csrfToken()} />
            <input type="hidden" name="_method" value="patch" />
            <input
              type="text"
              name="ai_action[name]"
              aria-label="Название"
              defaultValue={action.name}
              className={inputClass}
            />
            <textarea
              name="ai_action[prompt_template]"
              aria-label="Шаблон промпта"
              defaultValue={action.prompt_template}
              rows={3}
              className={inputClass}
            />
            <ScopeField defaultValue={action.scope} />
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <input type="hidden" name="ai_action[enabled]" value="0" />
              <input type="checkbox" name="ai_action[enabled]" value="1" defaultChecked={action.enabled} />
              Включено
            </label>
            <ConfirmField defaultChecked={action.confirm} />
            <ChannelsField action={action} />
            <div className="flex items-center gap-3">
              <button
                type="submit"
                className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded"
              >
                Сохранить
              </button>
              {action.is_seeded && (
                <span
                  className="text-xs text-gray-400"
                  title="Встроенное действие — нельзя удалить, можно только сбросить к исходному промпту"
                >
                  Встроенное действие
                </span>
              )}
            </div>
          </form>

          {action.is_seeded ? (
            <form
              method="POST"
              action={`/settings/ai_actions/${action.id}/reset`}
              onSubmit={(e) => {
                if (!window.confirm("Сбросить промпт к значению по умолчанию? Твои правки будут потеряны.")) e.preventDefault();
              }}
            >
              <input type="hidden" name="authenticity_token" value={csrfToken()} />
              <input type="hidden" name="_method" value="patch" />
              <button type="submit" className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                Сбросить
              </button>
            </form>
          ) : (
            <form
              method="POST"
              action={`/settings/ai_actions/${action.id}`}
              onSubmit={(e) => {
                if (!window.confirm("Удалить это действие?")) e.preventDefault();
              }}
            >
              <input type="hidden" name="authenticity_token" value={csrfToken()} />
              <input type="hidden" name="_method" value="delete" />
              <button type="submit" className="text-sm text-red-600 hover:text-red-700">
                Удалить
              </button>
            </form>
          )}
        </div>
      )}
    </li>
  );
}

export default function SettingsAiActions({
  aiActions,
  strategy = "",
  aiChatUrl,
  discussPrompt = "",
  discussPromptEnabled = true,
}: {
  aiActions: AiAction[];
  strategy?: string;
  aiChatUrl?: string;
  discussPrompt?: string;
  discussPromptEnabled?: boolean;
}) {
  const [creating, setCreating] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(false);
  const [editingChat, setEditingChat] = useState(false);
  const [chatDraft, setChatDraft] = useState(aiChatUrl ?? DEFAULT_AI_CHAT_URL);
  const [promptEnabled, setPromptEnabled] = useState(discussPromptEnabled);

  return (
    <SettingsLayout active="ai_actions" title="AI-действия">
      <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setEditingChat((v) => !v)}
          aria-expanded={editingChat}
          className={rowHeaderClass}
        >
          <span className="min-w-0">
            <span className="block text-sm font-medium text-gray-900 dark:text-white">Обсудить с ИИ</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Готовый промпт и куда на десктопе ведёт кнопка «поделиться» у задачи.
            </span>
          </span>
          <span className={editHintClass}>{editingChat ? "Закрыть" : "Изменить"}</span>
        </button>

        {editingChat && (
          <form method="POST" action="/settings/ai_chat" className="px-4 pb-4 space-y-3">
            <input type="hidden" name="authenticity_token" value={csrfToken()} />
            <input type="hidden" name="_method" value="patch" />

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <input type="hidden" name="discuss_prompt_enabled" value="0" />
                <input
                  type="checkbox"
                  name="discuss_prompt_enabled"
                  value="1"
                  checked={promptEnabled}
                  onChange={(e) => setPromptEnabled(e.target.checked)}
                />
                Добавлять промпт-инструкцию
              </label>
              <textarea
                name="discuss_prompt"
                aria-label="Промпт обсуждения"
                defaultValue={discussPrompt}
                disabled={!promptEnabled}
                maxLength={2000}
                rows={3}
                placeholder={DEFAULT_DISCUSS_PROMPT}
                className={`${inputClass} disabled:opacity-50`}
              />
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Добавляется перед контекстом задачи. Оставь пустым — используется значение по умолчанию.
              </p>
            </div>

            <div className="space-y-2">
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Чат — куда на десктопе ведёт «поделиться». Используй {"{prompt}"} для текста задачи.
              </span>
              <div className="flex flex-wrap gap-2">
                {chatPresets.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setChatDraft(p.url)}
                    className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                name="ai_chat_url"
                aria-label="URL чата ИИ"
                value={chatDraft}
                onChange={(e) => setChatDraft(e.target.value)}
                maxLength={500}
                placeholder={DEFAULT_AI_CHAT_URL}
                className={inputClass}
              />
            </div>

            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium text-sm"
            >
              Сохранить
            </button>
          </form>
        )}
      </section>

      <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setEditingStrategy((v) => !v)}
          aria-expanded={editingStrategy}
          className={rowHeaderClass}
        >
          <span className="min-w-0">
            <span className="block text-sm font-medium text-gray-900 dark:text-white">Стратегия</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Направляет «Спланировать день» при расстановке приоритетов в «Мой день».
            </span>
          </span>
          <span className={editHintClass}>{editingStrategy ? "Закрыть" : "Изменить"}</span>
        </button>

        {editingStrategy && (
          <form method="POST" action="/settings/strategy" className="px-4 pb-4 space-y-2">
            <input type="hidden" name="authenticity_token" value={csrfToken()} />
            <input type="hidden" name="_method" value="patch" />
            <textarea
              name="strategy"
              aria-label="Стратегия"
              defaultValue={strategy}
              maxLength={2000}
              rows={4}
              placeholder={`Что для тебя сейчас важно? Например: «Выпустить V2 в этом месяце, следить за здоровьем, не тонуть в поддержке».`}
              className={inputClass}
            />
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium text-sm"
            >
              Сохранить стратегию
            </button>
          </form>
        )}
      </section>

      {creating ? (
        <form
          method="POST"
          action="/settings/ai_actions"
          className="space-y-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4"
        >
          <input type="hidden" name="authenticity_token" value={csrfToken()} />
          <input
            type="text"
            name="ai_action[name]"
            aria-label="Название"
            placeholder="Название"
            className={inputClass}
            autoFocus
          />
          <textarea
            name="ai_action[prompt_template]"
            aria-label="Шаблон промпта"
            placeholder="Шаблон промпта"
            rows={3}
            className={inputClass}
          />
          <ScopeField />
          <ConfirmField />
          <ChannelsField />
          <div className="flex items-center gap-3">
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium text-sm"
            >
              Добавить действие
            </button>
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Отмена
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="w-full flex items-center justify-center gap-2 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-3 text-sm text-gray-500 hover:text-gray-800 hover:border-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600 transition-colors"
        >
          <span className="text-lg leading-none">+</span> Новое действие
        </button>
      )}

      {aiActions.length === 0 ? (
        <p className="text-gray-500">Пока нет AI-действий.</p>
      ) : (
        <ul className="space-y-3">
          {aiActions.map((a) => (
            <ActionRow key={a.id} action={a} />
          ))}
        </ul>
      )}
    </SettingsLayout>
  );
}
